import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { fetchMemberDonations } from 'services/membersApi';
import type { FetchMemberHistoryArgs, HistoryState } from 'types/history';
import type { MemberDonationsPage } from 'types/members';

const initialState: HistoryState = {
    records: [],
    totalCount: 0,
    summary: null,
    loading: false,
    error: null,
    latestRequestId: null,
};

/**
 * GET /v1/parishes/:parishId/members/:userId/donations — the parishioner Activity tab.
 * Resolves `null` when the selected parish changed mid-flight (the result belongs to another parish).
 */
export const fetchHistory = createAsyncThunkWrapper<MemberDonationsPage | null, FetchMemberHistoryArgs>(
    'history/fetchMemberDonations',
    async ({ parishId, userId, page, pageSize, from, to }: FetchMemberHistoryArgs, { getState }) => {
        if (!parishId || !userId) return null;
        try {
            const result = await fetchMemberDonations(parishId, userId, { page, pageSize, from, to });
            if (parishSelectionMoved(getState, parishId)) return null;
            return result;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    // The Activity tab shows the error in an inline alert with Retry — no toast on top of it.
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load giving history', toastOnError: false }
);

const historySlice = createSlice({
    name: 'historySlice',
    initialState,
    reducers: {
        clearHistory: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchHistory.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchHistory.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.latestRequestId) return; // superseded by a newer fetch
            state.loading = false;
            if (!action.payload) return;
            state.records = action.payload.items;
            state.totalCount = action.payload.total;
            state.summary = action.payload.summary;
        });
        builder.addCase(fetchHistory.rejected, (state, action) => {
            if (action.meta.requestId !== state.latestRequestId) return;
            state.loading = false;
            state.records = [];
            state.totalCount = 0;
            state.summary = null;
            state.error = (action.payload as string | undefined) || action.error?.message || 'Failed to load giving history';
        });
    },
});

export const { clearHistory } = historySlice.actions;
export default historySlice.reducer;
